import React, { useMemo, useState, type FC } from 'react'
import { useIntl } from 'react-intl'
import { useAtom } from 'jotai'
import {
  FormElement,
  Select,
  GridCell,
  InputNumber,
} from 'mailup-web-components'
import { filterFormImmerAtom } from '../../state/state'
import { type ConditionProps } from '../../types'
import {
  NumericFormat,
  type NumericMatch,
} from '../../../../types/FilterCondition'
import { numericMatchOpts } from '../../utils/conditionOptions'
import { type UiNumericSpec } from '../../filterConditionTypes'
import { validateNumericValue } from '../../utils/profileFieldSpecValidation'

const NumericSpecCondition: FC<ConditionProps> = ({
  blockIndex,
  condIndex,
}) => {
  const intl = useIntl()
  const [filter, setFilter] = useAtom(filterFormImmerAtom)
  const condition = filter.Blocks[blockIndex].Conditions[condIndex]
  const spec = condition.Spec as UiNumericSpec

  const matchOptions = useMemo(() => numericMatchOpts(intl), [intl])
  const [isValueDirty, setIsValueDirty] = useState(false)

  const handleOperatorChange = (op: NumericMatch): void => {
    setFilter((draft) => {
      const cond = draft.Blocks[blockIndex].Conditions[condIndex]
      ;(cond.Spec as UiNumericSpec) = {
        ...(cond.Spec as UiNumericSpec),
        Match: op,
        Value: 0,
      }
    })
  }

  const handleValueChange = (value: number): void => {
    setIsValueDirty(true)
    setFilter((draft) => {
      const spec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as UiNumericSpec
      spec.Value = value
    })
  }
  const invalidValueMessage =
    spec.Value === undefined
      ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
      : spec.Format === NumericFormat.Integer
        ? intl.formatMessage({ id: 'Load.Filters.InvalidInteger' })
        : intl.formatMessage({ id: 'Load.Filters.InvalidDecimal' })
  const feedback =
    isValueDirty && !validateNumericValue(spec)
      ? invalidValueMessage
      : undefined

  return (
    <>
      <GridCell span={3}>
        <FormElement>
          <Select
            placeholder={intl.formatMessage({
              id: 'Load.Filters.OperatorPlaceholder',
            })}
            options={matchOptions}
            value={spec.Match}
            onChange={handleOperatorChange}
            data-mu-test='input-select-operator'
          />
        </FormElement>
      </GridCell>
      {spec.Match !== undefined && (
        <GridCell span={3}>
          <FormElement
            data-mu-test='filterForm-UiNumericSpec-values_element'
            feedback={feedback}
          >
            <InputNumber
              data-mu-test='filterForm-UiNumericSpec-values_input'
              name='UiNumericSpec-values'
              value={spec.Value}
              onChange={handleValueChange}
              placeholder={intl.formatMessage({
                id: 'Load.Filters.InsertValuePlaceholder',
              })}
              maxLength={spec.Format === NumericFormat.Integer ? 8 : 15}
              precision={spec.Format === NumericFormat.Integer ? 0 : 2}
              step={spec.Format === NumericFormat.Integer ? 1 : 0.01}
              pattern={
                spec.Format === NumericFormat.Integer ? '\\d+' : '\\d+\\.\\d\\d'
              }
              decimalSeparator={
                spec.Format === NumericFormat.Integer ? undefined : '.'
              }
            />
          </FormElement>
        </GridCell>
      )}
    </>
  )
}

export default NumericSpecCondition
